import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { ScrollArea } from '../ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Users,
  Trophy,
  Gift,
  TrendingUp,
  Mail,
  Check,
  Clock,
  UserPlus,
  Crown,
  Settings,
  Save,
  Loader2,
  RefreshCw,
  Search,
  Award
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ReferralsManager() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [referrers, setReferrers] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [invites, setInvites] = useState([]);
  const [settings, setSettings] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [overviewRes, referrersRes, rewardsRes, invitesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/referrals/overview`),
        axios.get(`${API}/admin/referrals/referrers`),
        axios.get(`${API}/admin/referrals/rewards`),
        axios.get(`${API}/admin/referrals/invites?limit=50`),
        axios.get(`${API}/admin/referrals/settings`)
      ]);
      
      setOverview(overviewRes.data);
      setReferrers(referrersRes.data);
      setRewards(rewardsRes.data);
      setInvites(invitesRes.data);
      setSettings(settingsRes.data);
    } catch (err) {
      console.error('Failed to fetch referral data:', err);
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.post(`${API}/admin/referrals/settings`, null, {
        params: settings
      });
      toast.success('Referral settings saved');
      setSettingsOpen(false);
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleGrantReward = async (userId) => {
    try {
      await axios.post(`${API}/admin/referrals/manual-reward/${userId}`);
      toast.success('Reward granted successfully');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to grant reward');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return <Badge className="bg-blue-500/20 text-blue-400"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'registered':
        return <Badge className="bg-yellow-500/20 text-yellow-400"><UserPlus className="w-3 h-3 mr-1" />Registered</Badge>;
      case 'converted':
        return <Badge className="bg-green-500/20 text-green-400"><Check className="w-3 h-3 mr-1" />Converted</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-400">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#6D4AFF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Trophy className="w-7 h-7 text-amber-400" />
            Referral Program
          </h2>
          <p className="text-gray-400 mt-1">Track invites, conversions, and rewards</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchData}
            className="border-[#2d2d44] text-gray-300 hover:bg-[#2d2d44]"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={() => setSettingsOpen(true)}
            className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
          >
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-[#1a1a2e] border-[#2d2d44]">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-blue-500/20">
                <Mail className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{overview?.total_invites_sent || 0}</p>
                <p className="text-sm text-gray-400">Invites Sent</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#1a1a2e] border-[#2d2d44]">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-yellow-500/20">
                <UserPlus className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{overview?.total_registered || 0}</p>
                <p className="text-sm text-gray-400">Registrations</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#1a1a2e] border-[#2d2d44]">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-green-500/20">
                <TrendingUp className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{overview?.total_converted || 0}</p>
                <p className="text-sm text-gray-400">Conversions</p>
                <p className="text-xs text-green-400">{overview?.conversion_rate || 0}% rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#1a1a2e] border-[#2d2d44]">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-500/20">
                <Award className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{overview?.total_rewards_granted || 0}</p>
                <p className="text-sm text-gray-400">Rewards Granted</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#2d2d44] pb-2">
        {['overview', 'referrers', 'invites', 'rewards'].map((tab) => (
          <Button
            key={tab}
            variant={activeTab === tab ? 'default' : 'ghost'}
            onClick={() => setActiveTab(tab)}
            className={activeTab === tab ? 'bg-[#6D4AFF]' : 'text-gray-400 hover:text-white'}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </Button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Referrers */}
          <Card className="bg-[#1a1a2e] border-[#2d2d44]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-400" />
                Top Referrers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {overview?.top_referrers?.slice(0, 5).map((ref, index) => (
                  <div key={ref.user_id} className="flex items-center justify-between p-3 bg-[#252540] rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className={`text-lg font-bold ${index === 0 ? 'text-amber-400' : index === 1 ? 'text-gray-300' : index === 2 ? 'text-amber-600' : 'text-gray-500'}`}>
                        #{index + 1}
                      </span>
                      <div>
                        <p className="text-white font-medium">{ref.display_name}</p>
                        <p className="text-xs text-gray-400">{ref.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-green-400 font-bold">{ref.converted} paid</p>
                      <p className="text-xs text-gray-400">{ref.total_invites} sent</p>
                    </div>
                  </div>
                ))}
                {(!overview?.top_referrers || overview.top_referrers.length === 0) && (
                  <p className="text-gray-500 text-center py-4">No referrers yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Conversions */}
          <Card className="bg-[#1a1a2e] border-[#2d2d44]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                Recent Conversions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {overview?.recent_conversions?.slice(0, 5).map((conv, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-[#252540] rounded-lg">
                    <div>
                      <p className="text-white font-medium">{conv.invited_email}</p>
                      <p className="text-xs text-gray-400">Referred by {conv.inviter_name || conv.inviter_email}</p>
                    </div>
                    <Badge className="bg-green-500/20 text-green-400">
                      <Check className="w-3 h-3 mr-1" />
                      Paid
                    </Badge>
                  </div>
                ))}
                {(!overview?.recent_conversions || overview.recent_conversions.length === 0) && (
                  <p className="text-gray-500 text-center py-4">No conversions yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'referrers' && (
        <Card className="bg-[#1a1a2e] border-[#2d2d44]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white">All Referrers</CardTitle>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-[#252540] border-[#2d2d44] text-white w-64"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#2d2d44]">
                    <TableHead className="text-gray-400">User</TableHead>
                    <TableHead className="text-gray-400">Code</TableHead>
                    <TableHead className="text-gray-400 text-center">Sent</TableHead>
                    <TableHead className="text-gray-400 text-center">Registered</TableHead>
                    <TableHead className="text-gray-400 text-center">Converted</TableHead>
                    <TableHead className="text-gray-400 text-center">Rewards</TableHead>
                    <TableHead className="text-gray-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referrers
                    .filter(r => !searchQuery || 
                      r.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      r.referral_code.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map((ref) => (
                    <TableRow key={ref.user_id} className="border-[#2d2d44]">
                      <TableCell>
                        <div>
                          <p className="text-white font-medium">{ref.display_name}</p>
                          <p className="text-xs text-gray-400">{ref.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="text-[#6D4AFF] bg-[#6D4AFF]/10 px-2 py-1 rounded">
                          {ref.referral_code}
                        </code>
                      </TableCell>
                      <TableCell className="text-center text-gray-300">{ref.total_invites}</TableCell>
                      <TableCell className="text-center text-yellow-400">{ref.registered}</TableCell>
                      <TableCell className="text-center text-green-400">{ref.converted}</TableCell>
                      <TableCell className="text-center text-amber-400">{ref.rewards_earned}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleGrantReward(ref.user_id)}
                          className="text-amber-400 hover:text-amber-300 hover:bg-amber-400/10"
                        >
                          <Gift className="w-4 h-4 mr-1" />
                          Grant Reward
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {activeTab === 'invites' && (
        <Card className="bg-[#1a1a2e] border-[#2d2d44]">
          <CardHeader>
            <CardTitle className="text-white">All Invites</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#2d2d44]">
                    <TableHead className="text-gray-400">Invited Email</TableHead>
                    <TableHead className="text-gray-400">Inviter</TableHead>
                    <TableHead className="text-gray-400">Code</TableHead>
                    <TableHead className="text-gray-400">Status</TableHead>
                    <TableHead className="text-gray-400">Sent</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invites.map((inv) => (
                    <TableRow key={inv.id} className="border-[#2d2d44]">
                      <TableCell className="text-white">{inv.invited_email}</TableCell>
                      <TableCell>
                        <div>
                          <p className="text-gray-300">{inv.inviter_name}</p>
                          <p className="text-xs text-gray-500">{inv.inviter_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="text-[#6D4AFF] text-xs">{inv.referral_code}</code>
                      </TableCell>
                      <TableCell>{getStatusBadge(inv.status)}</TableCell>
                      <TableCell className="text-gray-400">
                        {new Date(inv.sent_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {activeTab === 'rewards' && (
        <Card className="bg-[#1a1a2e] border-[#2d2d44]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-400" />
              Granted Rewards
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#2d2d44]">
                    <TableHead className="text-gray-400">User</TableHead>
                    <TableHead className="text-gray-400">Reward</TableHead>
                    <TableHead className="text-gray-400">Milestone</TableHead>
                    <TableHead className="text-gray-400">Granted</TableHead>
                    <TableHead className="text-gray-400">Expires</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rewards.map((reward) => (
                    <TableRow key={reward.id} className="border-[#2d2d44]">
                      <TableCell>
                        <div>
                          <p className="text-white font-medium">{reward.user_name}</p>
                          <p className="text-xs text-gray-400">{reward.user_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-amber-500/20 text-amber-400">
                          <Trophy className="w-3 h-3 mr-1" />
                          1 Year {reward.plan_name}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-gray-300">
                        {reward.conversion_milestone} conversions
                      </TableCell>
                      <TableCell className="text-gray-400">
                        {new Date(reward.granted_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-gray-400">
                        {new Date(reward.expires_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                  {rewards.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                        No rewards granted yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="bg-[#1a1a2e] border-[#2d2d44]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Referral Program Settings
            </DialogTitle>
          </DialogHeader>
          {settings && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Max Invites Per Week</Label>
                <Input
                  type="number"
                  value={settings.max_invites_per_week}
                  onChange={(e) => setSettings({...settings, max_invites_per_week: parseInt(e.target.value)})}
                  className="bg-[#252540] border-[#2d2d44] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Conversions Required for Reward</Label>
                <Input
                  type="number"
                  value={settings.conversions_for_reward}
                  onChange={(e) => setSettings({...settings, conversions_for_reward: parseInt(e.target.value)})}
                  className="bg-[#252540] border-[#2d2d44] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Reward Plan Name</Label>
                <Input
                  value={settings.reward_plan_name}
                  onChange={(e) => setSettings({...settings, reward_plan_name: e.target.value})}
                  className="bg-[#252540] border-[#2d2d44] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Reward Duration (Days)</Label>
                <Input
                  type="number"
                  value={settings.reward_duration_days}
                  onChange={(e) => setSettings({...settings, reward_duration_days: parseInt(e.target.value)})}
                  className="bg-[#252540] border-[#2d2d44] text-white"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSettingsOpen(false)} className="text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleSaveSettings} disabled={savingSettings} className="bg-[#6D4AFF] hover:bg-[#5B3FD9]">
              {savingSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
